// SearchableSelect — reusable dropdown with type-ahead search.
//
// Drop-in replacement for a `<select>`:
//   <SearchableSelect
//     value={selectedId}
//     onChange={(id) => setSelectedId(id)}
//     options={[{ value: "1", label: "PN-A", hint: "Widget A" }, ...]}
//     placeholder="Pick a part no…"
//     disabled={false}
//     testId="cs-part-dropdown"
//   />
//
// - Filters options by label / hint / value (case-insensitive).
// - Keyboard: ↑/↓ moves, Enter selects, Esc closes.
// - Click outside closes. Clear-button resets to "".

import { useState, useRef, useEffect, useMemo, useCallback } from "react";
import { MagnifyingGlass, CaretDown, X } from "@phosphor-icons/react";

export default function SearchableSelect({
  value = "",
  onChange,
  options = [],
  placeholder = "Select…",
  disabled = false,
  testId,
  className = "",
  size = "md", // "sm" | "md"
  emptyText = "No matches",
  allowClear = true,
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [highlight, setHighlight] = useState(0);
  const rootRef = useRef(null);
  const searchRef = useRef(null);
  const listRef = useRef(null);

  const selected = useMemo(
    () => options.find(o => String(o.value) === String(value)) || null,
    [options, value]
  );

  const filtered = useMemo(() => {
    if (!query.trim()) return options;
    const q = query.trim().toLowerCase();
    return options.filter(o => {
      const hay = `${o.label ?? ""} ${o.hint ?? ""} ${o.value ?? ""}`.toLowerCase();
      return hay.includes(q);
    });
  }, [options, query]);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const onDown = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [open]);

  // Auto-focus search on open, reset highlight
  useEffect(() => {
    if (open) {
      setHighlight(0);
      setTimeout(() => searchRef.current?.focus(), 0);
    } else {
      setQuery("");
    }
  }, [open]);

  const pick = useCallback((opt) => {
    onChange?.(opt.value, opt);
    setOpen(false);
  }, [onChange]);

  const onKey = (e) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlight(h => Math.min(h + 1, Math.max(0, filtered.length - 1)));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlight(h => Math.max(h - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      const opt = filtered[highlight];
      if (opt) pick(opt);
    } else if (e.key === "Escape") {
      e.preventDefault();
      setOpen(false);
    }
  };

  // Scroll active option into view
  useEffect(() => {
    if (!open || !listRef.current) return;
    const el = listRef.current.querySelector(`[data-idx="${highlight}"]`);
    if (el) el.scrollIntoView({ block: "nearest" });
  }, [highlight, open]);

  const heightClass = size === "sm" ? "h-9" : "h-10";
  const textClass = size === "sm" ? "text-xs" : "text-sm";

  return (
    <div ref={rootRef} className={`relative ${className}`}>
      <button
        type="button"
        onClick={() => !disabled && setOpen(o => !o)}
        disabled={disabled}
        className={`w-full ${heightClass} border border-[#CBD5E1] rounded px-2.5 ${textClass} bg-white text-left flex items-center gap-2 disabled:bg-[#F1F5F9] disabled:cursor-not-allowed hover:border-[#94A3B8] focus:outline-none focus:ring-1 focus:ring-[#1D2951]`}
        data-testid={testId}
      >
        <span className={`flex-1 truncate ${selected ? "text-[#0F172A]" : "text-[#94A3B8]"}`}>
          {selected ? (selected.label ?? String(selected.value)) : placeholder}
        </span>
        {allowClear && selected && !disabled && (
          <span
            role="button"
            aria-label="Clear"
            onClick={(e) => { e.stopPropagation(); onChange?.("", null); }}
            className="p-0.5 rounded hover:bg-[#F1F5F9] text-[#94A3B8]"
            data-testid={testId ? `${testId}-clear` : undefined}
          >
            <X size={13} weight="bold" />
          </span>
        )}
        <CaretDown size={12} weight="bold" className={`text-[#64748B] transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <div className="absolute z-50 mt-1 w-full min-w-[220px] bg-white border border-[#E2E8F0] rounded-lg shadow-2xl overflow-hidden">
          {/* Search input */}
          <div className="flex items-center gap-2 px-2.5 py-1.5 border-b border-[#F1F5F9] bg-[#FAFBFC]">
            <MagnifyingGlass size={14} className="text-[#94A3B8]" />
            <input
              ref={searchRef}
              value={query}
              onChange={(e) => { setQuery(e.target.value); setHighlight(0); }}
              onKeyDown={onKey}
              placeholder="Search…"
              className="flex-1 bg-transparent outline-none text-xs placeholder:text-[#94A3B8] text-[#0F172A]"
              data-testid={testId ? `${testId}-search` : undefined}
            />
            <span className="text-[10px] text-[#94A3B8] font-mono shrink-0">{filtered.length}</span>
          </div>

          {/* Options list */}
          <div
            ref={listRef}
            className="max-h-64 overflow-y-auto py-1"
            data-testid={testId ? `${testId}-list` : undefined}
          >
            {filtered.length === 0 ? (
              <div className="px-3 py-4 text-center text-xs text-[#94A3B8]">{emptyText}</div>
            ) : (
              filtered.map((opt, idx) => {
                const isSelected = String(opt.value) === String(value);
                const isHi = idx === highlight;
                return (
                  <button
                    key={`${opt.value}-${idx}`}
                    type="button"
                    data-idx={idx}
                    onMouseEnter={() => setHighlight(idx)}
                    onClick={() => pick(opt)}
                    className={`w-full text-left px-3 py-1.5 text-xs transition-colors flex items-center gap-2 ${
                      isHi ? "bg-[#F5F3FF]" : ""
                    } ${isSelected ? "font-bold text-[#1D2951]" : "text-[#334155]"}`}
                  >
                    <span className="flex-1 truncate">
                      <span className="font-medium">{opt.label}</span>
                      {opt.hint && (
                        <span className="ml-2 text-[10px] text-[#94A3B8]">{opt.hint}</span>
                      )}
                    </span>
                    {isSelected && <span className="text-[10px] text-[#D4A843] font-bold">✓</span>}
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}
